import React, { FC, useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { Autocomplete, Chip, TextField } from "@mui/material";
import axiosInstant from "../../api";
import {
    listProjectPdpUrls,
    publicListProjectPdpUrls,
} from "../../api/endpoints";
import { PanelWrapper } from "./PanelWrapper";
import { FieldError } from "../fields/FieldError/FieldError";
import { IBriefPdpUrlEntry, normalizePdpUrl } from "./constants";

interface ProjectPdpOption {
    id: number;
    desireid: number;
    pdp_url: string;
    retailer?: string | null;
    active: boolean;
}

interface ProjectPdpProduct {
    desireid: number;
    title: string;
    options: ProjectPdpOption[];
}

interface Props {
    projectId?: number | string;
    body?: string;
    footer?: string;
    publicToken?: string;
    publicBase?: string;
}

// The brief form's other fields are tailwind-styled textareas; keep the
// autocomplete's input visually identical to them.
const INPUT_CLASSES =
    "!p-2 border border-surface-2 bg-surface-2 rounded-md " +
    "focus-within:bg-surface focus-within:border-accent " +
    "font-circular !text-[14px] text-text-1";

export const PdpUrlFields: FC<Props> = ({
    projectId,
    body,
    footer,
    publicToken,
    publicBase,
}) => {
    const { values, errors, touched, setFieldValue, setFieldTouched } =
        useFormikContext<any>();
    const [products, setProducts] = useState<ProjectPdpProduct[]>([]);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        let active = true;
        if (!projectId) return undefined;

        const url = publicToken
            ? publicListProjectPdpUrls(publicToken, projectId, publicBase)
            : listProjectPdpUrls(projectId);

        setLoadFailed(false);
        axiosInstant
            .get(url)
            .then(({ data }) => {
                if (!active) return;
                setProducts(Array.isArray(data?.products) ? data.products : []);
            })
            .catch(() => {
                // An empty dropdown and a failed request look identical to the
                // user, so say which one happened — they can still type a URL.
                if (active) setLoadFailed(true);
            });

        return () => {
            active = false;
        };
    }, [projectId, publicToken, publicBase]);

    const entries: IBriefPdpUrlEntry[] = useMemo(
        () => (Array.isArray(values.pdp_urls) ? values.pdp_urls : []),
        [values.pdp_urls]
    );

    // Reconcile the saved links against the project's actual products: a brief
    // written before a product was added (or before per-product links existed)
    // still has to open with one field per product.
    useEffect(() => {
        if (products.length === 0) return;

        const next = products.map((product) => {
            const saved = entries.find(
                (entry) => Number(entry.desireid) === product.desireid
            );
            return saved
                ? { ...saved, desireid: product.desireid }
                : {
                      desireid: product.desireid,
                      url: "",
                      desires_pdp_url_id: null,
                  };
        });

        // Legacy brief: a single `url` with no per-product rows belongs to the
        // primary product, which is the first one the API returns.
        if (!next.some((entry) => entry.url) && values.url) {
            next[0] = {
                ...next[0],
                url: values.url,
                desires_pdp_url_id: values.desires_pdp_url_id || null,
            };
        }

        if (JSON.stringify(next) !== JSON.stringify(entries)) {
            setFieldValue("pdp_urls", next);
        }
        // Runs when the product list arrives, not on every keystroke.
    }, [products]);

    const primaryDesireId = products.length > 0 ? products[0].desireid : null;

    const setEntry = (
        desireid: number | null,
        patch: Partial<IBriefPdpUrlEntry>
    ) => {
        if (desireid === null) {
            // No product list (fetch failed, or a project with no products):
            // fall back to the brief's single legacy URL.
            if (patch.url !== undefined) setFieldValue("url", patch.url);
            if (patch.desires_pdp_url_id !== undefined) {
                setFieldValue("desires_pdp_url_id", patch.desires_pdp_url_id);
            }
            return;
        }

        const next = products.map((product) => {
            const current = entries.find(
                (entry) => Number(entry.desireid) === product.desireid
            ) || {
                desireid: product.desireid,
                url: "",
                desires_pdp_url_id: null,
            };
            return product.desireid === desireid
                ? { ...current, ...patch, desireid }
                : current;
        });
        setFieldValue("pdp_urls", next);

        // The brief's own url/desires_pdp_url_id columns mirror the primary
        // product; the API does the same on write.
        if (desireid === primaryDesireId) {
            const primary = next[0];
            setFieldValue("url", primary.url);
            setFieldValue(
                "desires_pdp_url_id",
                primary.desires_pdp_url_id || null
            );
        }
    };

    const rows =
        products.length > 0
            ? products.map((product, index) => ({
                  key: String(product.desireid),
                  product,
                  index,
                  entry: entries.find(
                      (entry) => Number(entry.desireid) === product.desireid
                  ) || {
                      desireid: product.desireid,
                      url: "",
                      desires_pdp_url_id: null,
                  },
              }))
            : [
                  {
                      key: "primary",
                      product: null,
                      index: -1,
                      entry: {
                          desireid: null,
                          url: values.url || "",
                          desires_pdp_url_id: values.desires_pdp_url_id || null,
                      } as IBriefPdpUrlEntry,
                  },
              ];

    const errorFor = (index: number) => {
        if (index < 0) {
            return touched.url && typeof errors.url === "string"
                ? errors.url
                : "";
        }
        const list: any = errors.pdp_urls;
        const touchedList: any = touched.pdp_urls;
        const message = Array.isArray(list) ? list[index]?.url : undefined;
        const isTouched = Array.isArray(touchedList)
            ? touchedList[index]?.url
            : touched.url;
        return isTouched && typeof message === "string" ? message : "";
    };

    const markTouched = (index: number) => {
        setFieldTouched(index < 0 ? "url" : `pdp_urls[${index}].url`, true);
    };

    return (
        <PanelWrapper body={body} footer={footer}>
            {loadFailed && (
                <div className="font-circular text-[12px] text-error mb-1">
                    Saved PDP links couldn&apos;t be loaded. You can still paste
                    a URL below.
                </div>
            )}

            <div className="flex flex-col gap-4 w-full">
                {rows.map(({ key, product, index, entry }) => {
                    const options = product ? product.options : [];
                    const linked = options.find(
                        (option) => option.id === entry.desires_pdp_url_id
                    );
                    const error = errorFor(index);

                    const commit = (raw: string) => {
                        const url = normalizePdpUrl(raw);
                        const match = options.find(
                            (option) => option.pdp_url === url
                        );
                        setEntry(entry.desireid, {
                            url,
                            desires_pdp_url_id: match ? match.id : null,
                        });
                    };

                    return (
                        <div key={key} className="flex flex-col w-full">
                            {rows.length > 1 && product && (
                                <div className="font-circular text-[13px] font-bold text-text-1 mb-1">
                                    {product.title}
                                </div>
                            )}
                            <Autocomplete
                                freeSolo
                                options={options}
                                value={linked || entry.url || ""}
                                filterOptions={(list, state) =>
                                    list.filter((option) =>
                                        option.pdp_url
                                            .toLowerCase()
                                            .includes(
                                                state.inputValue
                                                    .trim()
                                                    .toLowerCase()
                                            )
                                    )
                                }
                                getOptionLabel={(option) =>
                                    typeof option === "string"
                                        ? option
                                        : option.pdp_url
                                }
                                isOptionEqualToValue={(option, value) =>
                                    typeof value === "string"
                                        ? option.pdp_url === value
                                        : option.id === value.id
                                }
                                onChange={(_event, next) => {
                                    if (!next) {
                                        setEntry(entry.desireid, {
                                            url: "",
                                            desires_pdp_url_id: null,
                                        });
                                        return;
                                    }
                                    if (typeof next === "string") {
                                        commit(next);
                                        return;
                                    }
                                    setEntry(entry.desireid, {
                                        url: next.pdp_url,
                                        desires_pdp_url_id: next.id,
                                    });
                                }}
                                onInputChange={(_event, input, reason) => {
                                    if (reason === "input") {
                                        setEntry(entry.desireid, {
                                            url: input,
                                            desires_pdp_url_id: null,
                                        });
                                    }
                                }}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <span className="font-circular text-[14px] text-text-1 break-all">
                                            {option.pdp_url}
                                        </span>
                                        {option.retailer && (
                                            <Chip
                                                label={option.retailer}
                                                size="small"
                                                className="!ml-2 !bg-primaryLight !text-[#7A5C24] !font-bold !text-[11px]"
                                            />
                                        )}
                                        {!option.active && (
                                            <Chip
                                                label="Inactive"
                                                size="small"
                                                className="!ml-2 !bg-[#F4F4F4] !text-[#8A8A8A] !text-[11px]"
                                            />
                                        )}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        placeholder="https://"
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                            className: `${
                                                params.InputProps.className || ""
                                            } ${INPUT_CLASSES}`,
                                        }}
                                        onBlur={(event) => {
                                            markTouched(index);
                                            commit(event.target.value);
                                        }}
                                    />
                                )}
                            />
                            {linked?.retailer && (
                                <div className="font-circular text-[12px] text-text-2 mt-2">
                                    Linked to the {linked.retailer} listing on
                                    this product
                                </div>
                            )}
                            {linked && !linked.active && (
                                <div className="font-circular text-[12px] text-text-2 mt-2">
                                    This PDP is inactive, so it is not being
                                    scored yet
                                </div>
                            )}
                            {error && <FieldError>{error}</FieldError>}
                        </div>
                    );
                })}
            </div>
        </PanelWrapper>
    );
};
